/**
 * @file Builds the column configuration for the investment's editable transaction table.
 */
import type { ReactNode } from "react";

import { Value } from "@components/Text";
import { hasAccess } from "@components/Authorization";
import { TransactionStatusTag } from "@components/Transaction";
import { getFormattedDecimalValue } from "@components/Amount";
import { getUsDateFromDate, getUsDateFromIsoDate } from "@components/Date";

import RowActions from "../RowActions";
import type { EditableTransaction, TransactionActions } from "./types";
import type { CollectionRef } from "../../../types";

const LABEL_AMOUNT_MIN_VALUE = "Minimum amount is $1";

const CENTS_IN_DOLLAR = 100;

const DATE_COLUMN_WIDTH = 100;
const AMOUNT_COLUMN_WIDTH = 170;
const ACTIONS_COLUMN_WIDTH = 52;

// A column of the editable collection; the input describes the form item
// of the editable row.
export type ColumnConfig = {
  title: string;
  width?: number;
  input?: Record<string, unknown>;
  render?: (value: unknown, record: EditableTransaction) => ReactNode;
  editable: boolean;
  dataIndex: string;
};

type Options = {
  collection: CollectionRef;
  createTransaction: TransactionActions["addTransactionAction"];
  deleteTransaction: TransactionActions["deleteTransactionAction"];
  isInvestmentFundedTemplateEnabled: TransactionActions["isInvestmentFundedTemplateEnabled"];
};

/**
 * Builds the date, description, amount, status and, when editable, actions columns for the transaction table.
 */
const getColumnsConfig = ({
  createTransaction: addTransactionAction,
  deleteTransaction: deleteTransactionAction,
  collection,
  isInvestmentFundedTemplateEnabled,
}: Options): ColumnConfig[] => {
  const isEditable = hasAccess([ "transactions-write" ]);

  const dateColumnProps: ColumnConfig = {
    title: "Date",
    editable: false,
    dataIndex: "date",
    /**
     * Formats the transaction date, or today's date for the new editable row.
     */
    render: (date: string) => (date
      ? getUsDateFromIsoDate(date)
      : getUsDateFromDate(new Date())),
  };

  const descriptionColumnProps: ColumnConfig = {
    title: "Description",
    editable: isEditable,
    dataIndex: "description",
    /**
     * Renders the transaction description, falling back to its details.
     */
    render: (_, { description, details }) => description || details,
    input: {
      type: "string",
      placeholder: "Add transaction description...",
      itemProps: {
        rules: [
          { required: false },
        ],
      },
    },
  };

  const amountColumnProps: ColumnConfig = {
    title: "Amount",
    /**
     * Renders the formatted transaction amount.
     */
    render: (amount: number) => <Value value={amount} />,
    editable: isEditable,
    dataIndex: "amount",
    width: AMOUNT_COLUMN_WIDTH,
    input: {
      type: "amount",
      required: true,
      itemProps: {
        rules: [
          {
            message: LABEL_AMOUNT_MIN_VALUE,
            /**
             * Rejects an amount that formats to zero or less.
             */
            validator: async (_: unknown, value: number | string) => {
              const formattedValue = getFormattedDecimalValue(value, CENTS_IN_DOLLAR, "times");
              const isValid = formattedValue > 0;

              if (!isValid) {
                throw new Error(LABEL_AMOUNT_MIN_VALUE);
              }
            },
          },
        ],
      },
      inputProps: {
        style: { width: "100%" },
      },
    },
  };

  const statusColumnProps: ColumnConfig = {
    title: "Status",
    editable: false,
    dataIndex: "status",
    /**
     * Renders the transaction status tag, or nothing when the transaction has no status.
     */
    render: (status: string, transaction) => {
      if (!status) {
        return undefined;
      }

      return <TransactionStatusTag transaction={{ ...transaction, status }} />;
    },
  };

  const columns: ColumnConfig[] = [
    { ...dateColumnProps, width: DATE_COLUMN_WIDTH },
    { ...descriptionColumnProps },
    { ...amountColumnProps },
    { ...statusColumnProps },
  ];

  if (!isEditable) {
    return columns;
  }

  const actionsColumnProps: ColumnConfig = {
    title: "",
    editable: false,
    dataIndex: "operation",
    /**
     * Renders the row's save or delete action.
     */
    render: (_, record) =>
      <RowActions
        record={record}
        collection={collection}
        isDeletable={record.isExternal || false}
        addTransactionAction={addTransactionAction}
        deleteTransactionAction={deleteTransactionAction}
        isInvestmentFundedTemplateEnabled={isInvestmentFundedTemplateEnabled}
      />,
  };

  return [
    ...columns,
    { ...actionsColumnProps, width: ACTIONS_COLUMN_WIDTH },
  ];
};

export default getColumnsConfig;
